import type { CSSProperties, FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { ChipValueFormatter } from '../../../../formatters/ChipValueFormatter/ChipValueFormatter';
import { useModifiers } from '../../../../styles/hooks/useModifiers';
import type { ChipViewProps } from '../Chip';

import { ChipDepth } from './ChipDepth';
import { createChipObserver, createValueObserver } from './lib';

import styles from './Chip3d.module.css';

interface CustomCSSProps extends CSSProperties {
  '--chipSize': string;
  '--tilt': string;
  '--tiltSin': string;
  '--tiltCos': string;
  '--chipBackground': string;
  '--rotate': string;
  '--rotateX': string;
  '--rotateY': string;
}

const chipObserver = createChipObserver();

const valueObserver = createValueObserver();

export const Chip3D: FC<ChipViewProps> = ({
  color,
  locale,
  size = 100,
  weight,
  value,
  postfix,
  tilt = 0,
  rotate = 0,
}) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);

  const mods = useModifiers(styles, 'chip', { grow: size === 'grow' });

  useEffect(() => {
    if (!chipRef.current || !valueRef.current) return;

    const chip = chipRef.current;
    const value = valueRef.current;
    chipObserver.observe(chip);
    valueObserver.observe(value);
    return () => {
      chipObserver.unobserve(chip);
      valueObserver.unobserve(value);
    };
  }, []);

  const tiltComponents = useMemo(() => {
    const tiltTransformed = (Math.PI / 2) * (1 - tilt);
    const tiltSin = Math.sin(tiltTransformed);
    const tiltCos = Math.cos(tiltTransformed);
    return { tiltSin, tiltCos, tilt };
  }, [tilt]);

  const rotateComponents = useMemo(() => {
    const rotationAngle = rotate * (Math.PI / 180);
    const rotateX = Math.sin(rotationAngle);
    const rotateY = Math.cos(rotationAngle);
    return { rotateX, rotateY, rotate };
  }, [rotate]);

  const computeStyles = useMemo((): CustomCSSProps => {
    const styles = {} as CustomCSSProps;

    if (size !== 'inherit' && size !== 'grow') {
      styles['--chipSize'] = `${size}px`;
    }

    styles['--tiltSin'] = `${tiltComponents.tiltSin}`;
    styles['--tiltCos'] = `${tiltComponents.tiltCos}`;
    styles['--tilt'] = `${tiltComponents.tilt}`;
    styles['--rotate'] = `${rotateComponents.rotate}deg`;
    styles['--rotateX'] = `${rotateComponents.rotateX}`;
    styles['--rotateY'] = `${rotateComponents.rotateY}`;

    if (color) {
      styles['--chipBackground'] = color;
    }

    return styles;
  }, [size, tiltComponents, rotateComponents, color]);

  return (
    <span className={`${styles.chip__container} ${styles[`chip__container_weight${weight}`]}`} style={computeStyles}>
      <span className={styles.chip__shadow} />
      {tilt ? <ChipDepth rotate={rotate} tiltComponents={tiltComponents} /> : null}
      <span className={styles.chip__surface} />
      <span className={`${styles.chip} ${mods}`} ref={chipRef}>
        <span className={styles.chip__valueContainer}>
          <span className={styles.chip__value} ref={valueRef}>
            {ChipValueFormatter.format(locale, value)}
            {postfix}
          </span>
        </span>
      </span>
      <span className={styles.chip__glare} />
    </span>
  );
};
